import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';

import { elevation } from 'settings/tokens';

import { GatePresentationProvider } from './gatePresentation';
import type { GatePresentation } from './gatePresentation';

/**
 * The access gate as a full-bleed surface IN THE PAGE FLOW. `?present=sheet`
 * selects it.
 *
 * ## Why this is not a modal sheet, having built one and measured it
 *
 * A modal sheet is pinned to the viewport, so its height and the page's content
 * compete for the same 812px. Measured on this record at 375x812:
 *
 *     brand header    72px
 *     hero image     375px
 *     title band      72px
 *     the form       572px
 *     ------------------------
 *     total         1091px   against a 812px viewport
 *
 * The form alone is 70% of the screen. So a pinned sheet has exactly two options
 * and both were rejected on sight: size to content and it starts at y=240, burying
 * the product photograph and the title — the two things that make the ask read as
 * "there is something here, and this is what it costs" rather than as a form from
 * nowhere; or cap it below the title and 279 of its 572px go behind an INTERNAL
 * scroll, a scrollbar inside a scrollbar with the page frozen behind it.
 *
 * There is no third arrangement. 1091 does not fit in 812, so something has to
 * move, and the only thing that can move without hiding content is the PAGE.
 * In the flow, the hero and title are on screen at rest, and scrolling brings the
 * gate up to fill the viewport — one scroll, the one the user already expects.
 *
 * ⚠️ This is substantially the panel deleted in `03c06d0e1`, and that is worth
 * saying plainly rather than presenting it as something new: the A/B that deleted
 * it recorded the panel's cost as covering the blurred placeholder rows, and on
 * this record `GatedDetailsPlaceholder` renders at **343x0px** — there is nothing
 * behind the ask in EITHER presentation, so that cost was not being paid.
 *
 * ## The edge, which took four goes
 *
 * 1. 16px top radius with `borderTop` ONLY. A radius curves the top border down
 *    into the left and right edges, so with no side border to meet, the hairline
 *    swept through the arc and ended in mid air — over white-on-white, because an
 *    in-flow surface with no shadow has nothing behind it for the corner to reveal.
 * 2. Squared it. That fixed the stray hairline and cost the whole signal: a square
 *    full-bleed edge is a SECTION DIVIDER, which is exactly how the page read.
 * 3. Radius back, plus side borders so the arc closes, plus the shadow. The top
 *    was right and the BOTTOM was the same bug one end down — the side hairlines
 *    ran to the surface's content height and stopped in space above the footer,
 *    because this surface's height is content, not the viewport (below).
 * 4. No stroke at all. The radius and the shadow carry the edge, which is how a
 *    real bottom sheet is drawn — iOS sheets have a radius and a shadow and no
 *    outline. A stroke on a surface that runs off both sides and ends on content
 *    has to terminate somewhere, and every somewhere here is mid-air; the shadow
 *    has no ends to terminate. `elevation.raisedUp` carries a near layer for
 *    exactly this reason — without a stroke, the near layer IS the edge.
 *
 * ## Filling to the bottom without a magic number
 *
 * The surface's top depends on the hero's rendered height, the title's length and
 * the viewport, so any constant is right on exactly one device. It is MEASURED.
 *
 * `dvh`, not `vh`: on mobile Safari `vh` is the tallest the viewport ever gets, so
 * with the URL bar shown the surface would overhang the bottom by its height.
 *
 * ⚠️ `useLayoutEffect` + listeners, deliberately NOT a `ResizeObserver`. The Claude
 * preview pane fires ZERO `ResizeObserver` callbacks, so an observer could never
 * run there and the surface would render at its content height with no way to tell
 * from a screenshot that the sizing code had not executed.
 *
 * The `load` listener is the one that is easy to omit and costly to: the hero is a
 * large image, so on a cold load the surface sits high until it decodes and then
 * drops. It is attached to this element's own ROOT NODE (the app renders inside a
 * shadow root) in the CAPTURE phase, because `load` does not bubble.
 */
export const GateSurface = ({
  presentation,
  children,
}: {
  presentation: GatePresentation;
  /** Accepted and ignored. The gate renders its own heading in both presentations
      now — there is no drawer header to collide with — and these are kept so the
      call sites read the same. */
  title?: string;
  mandatory?: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [minHeight, setMinHeight] = useState<string | undefined>(undefined);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    /* `top` relative to the DOCUMENT, not the viewport, so a page that is already
       scrolled when this runs does not shrink the surface by its own scroll offset. */
    const top = el.getBoundingClientRect().top + window.scrollY;
    setMinHeight(`calc(100dvh - ${Math.round(top)}px)`);
  }, []);

  useLayoutEffect(() => {
    if (presentation !== 'sheet') return;
    measure();
    const root = ref.current?.getRootNode() as Document | ShadowRoot | undefined;
    window.addEventListener('resize', measure);
    root?.addEventListener('load', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      root?.removeEventListener('load', measure, true);
    };
  }, [presentation, measure]);

  if (presentation === 'card') {
    return <GatePresentationProvider value="card">{children}</GatePresentationProvider>;
  }

  return (
    <GatePresentationProvider value="sheet">
      <Box
        ref={ref}
        sx={{
          /* FULL BLEED. The page gutter is 16px (`px: 2` on the 8px base apps/app
             uses — working-conventions §29, where this app does NOT get the 4px base
             `@repo/ui` sets), so pull out by exactly that on each side. */
          mx: -2,
          width: 'calc(100% + 32px)',
          /* Clear of the title, because the SHADOW needs the room, not the surface.
             `raisedUp`'s ambient layer is offset 12px up and blurred 28px, so it
             reaches 12 + 14 = 26px above this edge. At the title's natural 16px gap
             that put the darkest part of the lift straight across the product name.
             16px more (the 8px base this app uses, §29) sits the edge 32px down, so
             the shadow tops out ~6px clear of the title's text box -- measured at
             375x812, not estimated. */
          mt: 2,
          minHeight,
          backgroundColor: 'background.paper',
          /* The one boundary this surface has, and it carries BOTH signals the state
             card family uses: the border draws the edge, the shadow says the surface
             sits ON the certificate rather than being part of it
             (`stateCardSurface`). Cast upward — see `elevation.raisedUp`.

             ⚠️ The lift is doing more work here than the token's own docblock
             assumes. It says cards need lift "because the page surface defaults to
             `#F7F7F8` rather than white" — measured on THIS page, the surface behind
             the gate is `rgb(255,255,255)`. White on white, the 1px rule was the only
             separation, which is exactly why the gate read as page content. */
          /* NO border — see the corner note above. The radius and the shadow carry
             the edge; a stroke has to terminate somewhere and every somewhere on
             this surface is mid-air. */
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          boxShadow: elevation.raisedUp,
          /* The gutter the full bleed just removed, given back INSIDE, so the form is
             full-width without its text touching the screen edge. */
          px: 2,
          pt: 2.5,
          pb: 4,
        }}
      >
        {children}
      </Box>
    </GatePresentationProvider>
  );
};
